import { useEffect, useState } from 'react';
import Select from 'react-select';

import { YEAR_START, MONTHS_OPTION } from '@/common/constants';
import { getCustomStyles } from '@/utils/customStyleSelect';

import './dashboard.css';
import { getCurrentMonth } from '@/utils/date';

type onChangeMonthSelector = (month: number, year: number) => void;

type MonthSelectorProps = {
    onChange: onChangeMonthSelector,
    month: number,
    year: number,
    allowAllMonths?: boolean, // if true, an "all" option is available on month dropdown
    disabled?: boolean,
    isStacked?: boolean, // if true, month is above year, if false, month is next to year (if possible)
    color?: string,
};

type Option = {
    value: number,
    label: string,
};

const MonthSelector = ({ onChange, month, year, allowAllMonths = true, disabled = false, isStacked = false, color = '#ffffff' }: MonthSelectorProps) => {
    const {year: currentYear, month: currentMonth} = getCurrentMonth();

    const STYLES = {
        MONTH: { display: 'inline-block', width: '10em', backgroundColor: color },
        YEAR: { display: 'inline-block', width: '7em', backgroundColor: color },
        STACKED: { display: 'block', width: '100%', backgroundColor: color },
    };

    const option = (isMonth: boolean, value: number): Option => {
        if (isMonth) {
            return { value, label: MONTHS_OPTION[value] };
        }
        return { value, label: String(value) }
    };

    const monthsWithAll = MONTHS_OPTION.map((_month, monthIndex) => option(true, monthIndex));
    const monthsNoAll = monthsWithAll.filter((option) => (option.value > 0));

    const [selectedYear, setSelectedYear] = useState<Option>(option(false, year ?? currentYear));
    const [selectedMonth, setSelectedMonth] = useState<Option>(option(true, month ?? currentMonth));

    const getYearFromStart = () => {
        const length = currentYear + 1 - YEAR_START;
        const years = Array.from(Array(length).keys()).map((value) => (YEAR_START + value));
        return years;
    };

    const years = getYearFromStart();

    useEffect(() => {
        onChange(selectedMonth.value, selectedYear.value);
    }, [selectedMonth, selectedYear]);

    useEffect(() => {
        setSelectedMonth(option(true, month));
    }, [month]);

    useEffect(() => {
        setSelectedYear(option(false, year));
    }, [year]);

    return (
        <div className='month-selector-container'>
            <div style={isStacked ? STYLES.STACKED : STYLES.YEAR}>
                <Select
                    name="year"
                    value={selectedYear}
                    defaultValue={option(false, currentYear)}
                    onChange={(newValue) => setSelectedYear(option(false, newValue?.value ?? currentYear))}
                    options={years.map((year) => option(false, year))}
                    styles={getCustomStyles(false)}
                    isDisabled={disabled}
                />
            </div>
            <div style={isStacked ? STYLES.STACKED : STYLES.MONTH}>
                <Select
                    name="month"
                    value={selectedMonth}
                    defaultValue={option(true, currentMonth)}
                    onChange={(newValue) => setSelectedMonth(option(true, newValue?.value ?? currentMonth))}
                    options={allowAllMonths ? monthsWithAll : monthsNoAll}
                    styles={getCustomStyles(false)}
                    isDisabled={disabled}
                />
            </div>
        </div>
    );
};

export default MonthSelector;
